import { evaluate } from "@kyan/ui";

import { match } from "./match";

type RoundToNearestSide = "bid" | "ask" | "buy" | "sell" | boolean;
type RoundToNearestSideNormalized = "buy" | "sell" | undefined;

function normalizeSide(
  side?: RoundToNearestSide,
): RoundToNearestSideNormalized {
  if (side === undefined) return undefined;

  return evaluate({
    buy: side === true || ["bid", "buy"].includes(side as string),
    sell: side === false || ["ask", "sell"].includes(side as string),
  });
}

/**
 * Rounds `value` to a multiple of `multiple`.
 *
 * - By default, rounds to the nearest tick (`Math.round`).
 * - If `side` is buy-side (`"bid" | "buy" | true`), rounds down (`Math.floor`).
 * - If `side` is sell-side (`"ask" | "sell" | false`), rounds up (`Math.ceil`).
 *
 * For `multiple < 1`, the result is formatted to avoid floating-point precision issues.
 */
export function roundToNearest(
  value: number,
  multiple: number,
  side?: RoundToNearestSide,
) {
  const normalizedSide = normalizeSide(side);
  const roundFn =
    match([
      [Math.floor, normalizedSide === "buy"],
      [Math.ceil, normalizedSide === "sell"],
    ]) ?? Math.round;

  const rounded = roundFn(value / multiple) * multiple;
  return multiple < 1
    ? parseFloat(rounded.toFixed(-Math.floor(Math.log10(multiple))))
    : rounded;
}

export const isNumeric = (value?: unknown): value is number | bigint =>
  (typeof value === "number" && Number.isFinite(value)) ||
  typeof value === "bigint";
